import { useState, useEffect, useMemo, useRef } from 'react';
import type { AppState } from '@strands/agent-transport';

interface Attachment {
  path: string;
  name?: string;
}

interface EmailDraftState {
  to?: string;
  cc?: string;
  bcc?: string;
  subject?: string;
  body?: string;
  attachments?: Attachment[];
  updated_at?: number;
}

interface WorkspaceFile {
  path: string;
  size?: number;
}

interface Props {
  /** AG-UI session state — reads `state.email_draft` (agent's draft) and
   * `state.files` (workspace files for the attachment picker). */
  state: AppState;
  /** Local state mutator — writes the user's edits back to
   * `state.email_draft`. The new value travels to the backend on the next
   * `sendMessage` via `RunAgentInput.state`; the agent picks it up via
   * `email_draft_read` on its next turn. */
  updateState: (key: string, value: unknown) => void;
  /** MainSlot wires this to closeMain. */
  onClose: () => void;
}

const KIB = 1024;
const MIB = 1024 * KIB;

function basename(path: string): string {
  return path.split('/').pop() || path;
}

function formatSize(bytes: number | undefined): string {
  if (bytes === undefined) return '';
  if (bytes < KIB) return `${bytes} Б`;
  if (bytes < MIB) return `${Math.round(bytes / KIB)} КБ`;
  return `${(bytes / MIB).toFixed(1)} МБ`;
}

function normalize(d: EmailDraftState | undefined) {
  return {
    to: d?.to ?? '',
    cc: d?.cc ?? '',
    bcc: d?.bcc ?? '',
    subject: d?.subject ?? '',
    body: d?.body ?? '',
    attachments: d?.attachments ?? [],
  };
}

/**
 * Shared email-draft composer in the main slot — state-canvas pattern.
 *
 * The agent writes a draft via the `compose_email` tool, which sets
 * `state.email_draft` (one bump = one `updated_at` increment). This view
 * renders the draft as an editable form; the user can tweak any field
 * and saves locally — `updateState` writes to the frontend state dict,
 * and the new value ships to the backend on the next `sendMessage` via
 * `RunAgentInput.state`. The agent picks it up via `email_draft_read`
 * on its next turn.
 *
 * Edit-mode contention: while the user has unsaved local changes (`dirty`),
 * incoming agent updates to `state.email_draft` are ignored — otherwise an
 * agent that re-drafts mid-edit would nuke the user's in-progress text.
 * When the user saves (or hasn't touched anything), the view reflects the
 * latest from state.
 *
 * Same shape as CanvasView, just with structured fields instead of a
 * single markdown blob.
 */
export function MailComposeView({ state, updateState, onClose }: Props) {
  const remote = (state.email_draft as EmailDraftState | undefined) ?? {};

  const [to, setTo] = useState(remote.to ?? '');
  const [cc, setCc] = useState(remote.cc ?? '');
  const [bcc, setBcc] = useState(remote.bcc ?? '');
  const [subject, setSubject] = useState(remote.subject ?? '');
  const [body, setBody] = useState(remote.body ?? '');
  const [attachments, setAttachments] = useState<Attachment[]>(remote.attachments ?? []);
  const [showCcBcc, setShowCcBcc] = useState(!!(remote.cc || remote.bcc));
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState('');
  const pickerRef = useRef<HTMLDivElement>(null);

  const workspaceFiles = useMemo<WorkspaceFile[]>(() => {
    const raw = state.files;
    return Array.isArray(raw) ? (raw as WorkspaceFile[]) : [];
  }, [state.files]);

  const attachedPaths = useMemo(() => new Set(attachments.map((a) => a.path)), [attachments]);

  const pickerCandidates = useMemo(() => {
    const q = pickerQuery.trim().toLowerCase();
    return workspaceFiles
      .filter((f) => !attachedPaths.has(f.path))
      .filter((f) => !q || f.path.toLowerCase().includes(q))
      .slice(0, 50);
  }, [workspaceFiles, attachedPaths, pickerQuery]);

  // `dirty` — local edits diverge from what the agent last wrote. JSON
  // compare is fine for this shape (small, plain values + a list of objects).
  const dirty = useMemo(() => {
    const remoteNorm = normalize(remote);
    const draftNorm = { to, cc, bcc, subject, body, attachments };
    return JSON.stringify(draftNorm) !== JSON.stringify(remoteNorm);
  }, [to, cc, bcc, subject, body, attachments, remote]);

  // Resync local form from state when NOT dirty — keeps the view live as
  // the agent rewrites the draft. While dirty, ignore incoming updates to
  // avoid nuking the user's in-progress edits.
  useEffect(() => {
    if (dirty) return;
    setTo(remote.to ?? '');
    setCc(remote.cc ?? '');
    setBcc(remote.bcc ?? '');
    setSubject(remote.subject ?? '');
    setBody(remote.body ?? '');
    setAttachments(remote.attachments ?? []);
    if (remote.cc || remote.bcc) setShowCcBcc(true);
    // We watch `remote.updated_at` as the version cursor so this only
    // fires when the agent actually rewrites the draft, not on every
    // unrelated state delta.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remote.updated_at, dirty]);

  // Close picker on outside click.
  useEffect(() => {
    if (!pickerOpen) return;
    const onClick = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [pickerOpen]);

  const addAttachment = (file: WorkspaceFile) => {
    setAttachments((prev) =>
      prev.find((a) => a.path === file.path)
        ? prev
        : [...prev, { path: file.path, name: basename(file.path) }],
    );
    setPickerQuery('');
    setPickerOpen(false);
  };

  const removeAttachment = (path: string) => {
    setAttachments((prev) => prev.filter((a) => a.path !== path));
  };

  const save = () => {
    updateState('email_draft', {
      to,
      cc,
      bcc,
      subject,
      body,
      attachments,
      updated_at: Date.now() / 1000,
    });
  };

  const reset = () => {
    setTo(remote.to ?? '');
    setCc(remote.cc ?? '');
    setBcc(remote.bcc ?? '');
    setSubject(remote.subject ?? '');
    setBody(remote.body ?? '');
    setAttachments(remote.attachments ?? []);
  };

  return (
    <div className="mail-compose-view">
      <div className="mail-compose-view-inner">
        <div className="mail-compose-field">
          <label className="mail-compose-label">Кому</label>
          <input
            className="mail-compose-input"
            type="text"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder="name@example.com"
            autoComplete="off"
            spellCheck={false}
          />
          {!showCcBcc && (
            <button
              type="button"
              className="mail-compose-toggle"
              onClick={() => setShowCcBcc(true)}
            >
              Копия
            </button>
          )}
        </div>

        {showCcBcc && (
          <>
            <div className="mail-compose-field">
              <label className="mail-compose-label">Копия</label>
              <input
                className="mail-compose-input"
                type="text"
                value={cc}
                onChange={(e) => setCc(e.target.value)}
                placeholder="email@example.com"
                autoComplete="off"
                spellCheck={false}
              />
            </div>
            <div className="mail-compose-field">
              <label className="mail-compose-label">Скрытая</label>
              <input
                className="mail-compose-input"
                type="text"
                value={bcc}
                onChange={(e) => setBcc(e.target.value)}
                placeholder="email@example.com"
                autoComplete="off"
                spellCheck={false}
              />
            </div>
          </>
        )}

        <div className="mail-compose-field">
          <label className="mail-compose-label">Тема</label>
          <input
            className="mail-compose-subject"
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Тема письма"
          />
        </div>

        <div className="mail-compose-field mail-compose-attach-row">
          <label className="mail-compose-label">Файлы</label>
          <div className="mail-compose-attach-area">
            {attachments.map((a) => (
              <span key={a.path} className="mail-compose-chip" title={a.path}>
                <ClipIcon />
                <span className="mail-compose-chip-name">{a.name || basename(a.path)}</span>
                <button
                  type="button"
                  className="mail-compose-chip-remove"
                  onClick={() => removeAttachment(a.path)}
                  aria-label="Убрать"
                >
                  ×
                </button>
              </span>
            ))}
            <div className="mail-compose-attach-picker" ref={pickerRef}>
              <button
                type="button"
                className={`mail-compose-attach-btn${attachments.length > 0 ? ' mail-compose-attach-btn--plus' : ''}`}
                onClick={() => setPickerOpen((v) => !v)}
                aria-label={attachments.length > 0 ? 'Добавить файл' : undefined}
              >
                {attachments.length > 0 ? (
                  <span aria-hidden>+</span>
                ) : (
                  <>
                    <ClipIcon />
                    <span>Прикрепить</span>
                  </>
                )}
              </button>
              {pickerOpen && (
                <div className="mail-compose-attach-dropdown" role="listbox">
                  <input
                    type="text"
                    className="mail-compose-attach-search"
                    placeholder="Поиск по файлам…"
                    value={pickerQuery}
                    onChange={(e) => setPickerQuery(e.target.value)}
                    autoFocus
                  />
                  <div className="mail-compose-attach-list">
                    {workspaceFiles.length === 0 ? (
                      <div className="mail-compose-attach-empty">В рабочем пространстве нет файлов</div>
                    ) : pickerCandidates.length === 0 ? (
                      <div className="mail-compose-attach-empty">Ничего не найдено</div>
                    ) : (
                      pickerCandidates.map((f) => (
                        <button
                          key={f.path}
                          type="button"
                          className="mail-compose-attach-item"
                          onClick={() => addAttachment(f)}
                        >
                          <span className="mail-compose-attach-item-name">{basename(f.path)}</span>
                          <span className="mail-compose-attach-item-meta">
                            {f.path !== basename(f.path) && (
                              <span className="mail-compose-attach-item-path">{f.path}</span>
                            )}
                            {f.size !== undefined && (
                              <span className="mail-compose-attach-item-size">{formatSize(f.size)}</span>
                            )}
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mail-compose-field mail-compose-field--body">
          <label className="mail-compose-label mail-compose-label--body">Текст</label>
          <textarea
            className="mail-compose-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Текст письма…"
          />
        </div>
      </div>

      <div className="mail-compose-footer">
        {dirty ? (
          <>
            <span className="mail-compose-hint">Агент увидит ваши правки в следующем ответе</span>
            <button
              type="button"
              className="mail-compose-secondary"
              onClick={reset}
            >
              Отмена
            </button>
            <button
              type="button"
              className="mail-compose-submit"
              onClick={save}
            >
              <span>Сохранить</span>
            </button>
          </>
        ) : (
          <button
            type="button"
            className="mail-compose-secondary"
            onClick={onClose}
          >
            Закрыть
          </button>
        )}
      </div>
    </div>
  );
}

function ClipIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
    </svg>
  );
}
